import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MoonStar, Play } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export const QuietDaysCard = ({ token }) => {
  const [active, setActive] = useState(null);
  const [range, setRange] = useState({ start_date: todayISO(), end_date: plusDaysISO(7) });
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/rhythm/quiet`, headers).then((r) => setActive(r.data.active)).catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const pause = async () => {
    try {
      const res = await axios.post(`${API}/rhythm/quiet`, range, headers);
      setActive(res.data.active);
      toast.success("Quiet days set — reminders paused, streak protected 🍃");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set quiet days");
    }
  };

  const resume = async () => {
    try {
      await axios.delete(`${API}/rhythm/quiet`, headers);
      setActive(null);
      toast.success("Welcome back — reminders resumed 🌼");
    } catch (e) {
      toast.error("Couldn't resume");
    }
  };

  return (
    <Card className="glass-card rounded-3xl border-0 mb-6" data-testid="quiet-days-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-4 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
            <MoonStar className="w-6 h-6 text-teal-600" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground">Quiet Days</h3>
            <p className="text-sm text-muted-foreground">
              Going on holiday? Pause all reminders — your streak stays safe the whole time.
            </p>
          </div>
        </div>
        {active ? (
          <div className="flex items-center justify-between gap-3 bg-teal-50 dark:bg-teal-900/20 rounded-xl p-3" data-testid="quiet-active">
            <p className="text-sm text-teal-700 dark:text-teal-300 font-medium">
              🍃 Quiet until {active.end_date} — streak protected
            </p>
            <Button variant="outline" size="sm" className="rounded-full flex-shrink-0" onClick={resume} data-testid="quiet-resume-btn">
              <Play className="w-3.5 h-3.5 mr-1.5" /> Resume now
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">From</label>
              <input
                type="date"
                min={todayISO()}
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
                value={range.start_date}
                onChange={(e) => setRange((p) => ({ ...p, start_date: e.target.value }))}
                data-testid="quiet-start-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Until</label>
              <input
                type="date"
                min={range.start_date}
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
                value={range.end_date}
                onChange={(e) => setRange((p) => ({ ...p, end_date: e.target.value }))}
                data-testid="quiet-end-input"
              />
            </div>
            <Button size="sm" className="rounded-full" onClick={pause} data-testid="quiet-pause-btn">
              <MoonStar className="w-3.5 h-3.5 mr-1.5" /> Pause reminders
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
